import { ImgID } from "../../image_loader/img_id.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { OFFLINE_ADS_TOOLTIP_KEY } from "../../ads/offline_ads.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";

const DESIGN_W = 720;
const DESIGN_H = 200;
const DESIGN_SIDE_MARGIN = 30;
const DESIGN_TEXT_PAD_X = 50;
const DESIGN_TEXT_PAD_Y = 30;
const DESIGN_TAIL_W = 111;
const DESIGN_TAIL_H = 78;
const DESIGN_TAIL_Y_OFFSET = -32;
const DESIGN_ANCHOR_GAP = 8;

type OpenTooltip = {
  root: UIElement;
  dismiss_layer: UIElementHolder;
  body: UIElementHolder;
};

let opened: OpenTooltip | null = null;

export const ADS_UNAVAILABLE_TOOLTIP_KEY = 'ADS.UNAVAILABLE.TRY_LATER';

export function showOfflineAdTooltip( anchor: UIElement ): void
{
  showAdTooltip( anchor, OFFLINE_ADS_TOOLTIP_KEY );
}

export function showAdUnavailableTooltip( anchor: UIElement ): void
{
  showAdTooltip( anchor, ADS_UNAVAILABLE_TOOLTIP_KEY );
}

function showAdTooltip( anchor: UIElement, text_key: string ): void
{
  hideOfflineAdTooltip();

  const root = anchor.getRootElement();
  const ctx = root.getContext();
  const root_rect = root.getAbsoluteRect();
  const anchor_rect = anchor.getAbsoluteRect();
  const root_w = root.getRect().getW();
  const root_h = root.getRect().getH();

  const w = LayoutScaler.widthFromDesign( DESIGN_W );
  const h = LayoutScaler.heightFromDesign( DESIGN_H );
  const tail_w = LayoutScaler.widthFromDesign( DESIGN_TAIL_W );
  const tail_h = LayoutScaler.heightFromDesign( DESIGN_TAIL_H );
  const tail_y_offset = LayoutScaler.heightFromDesign( DESIGN_TAIL_Y_OFFSET );
  const side_margin = LayoutScaler.widthFromDesign( DESIGN_SIDE_MARGIN );
  const anchor_gap = LayoutScaler.heightFromDesign( DESIGN_ANCHOR_GAP );

  const anchor_center_x = anchor_rect.x - root_rect.x + anchor_rect.width / 2;
  const anchor_top = anchor_rect.y - root_rect.y;

  const x = Math.max( side_margin, Math.min( anchor_center_x - w / 2, root_w - side_margin - w ) );
  const visual_h = h + tail_y_offset + tail_h;
  const y = Math.max( 0, anchor_top - anchor_gap - visual_h );

  const dismiss_layer = new UIElementHolder( ctx, 0, 0, root_w, root_h );
  dismiss_layer.setOnClick( () => hideOfflineAdTooltip(), true );

  const body = new UIElementHolder( ctx, x, y, w, visual_h );
  body.setClickable( false );

  const substrate = new UINineSliceElement( ctx, 0, 0, w, h, ImgID.UI_TutorialSubstrate, "tutorial_substrate" );
  body.addChild( substrate, true );

  const pad_x = LayoutScaler.widthFromDesign( DESIGN_TEXT_PAD_X );
  const pad_y = LayoutScaler.heightFromDesign( DESIGN_TEXT_PAD_Y );
  const text = new UITextElement( ctx, pad_x, pad_y, w - pad_x * 2, h - pad_y * 2, {
    text: LocalizationManager.t( text_key ),
    font_size: LayoutScaler.textSizeFromDesign( 44 ),
    font_weight: 900,
    text_color: ColorScheme.tooltip_text_outline,
    text_style: TextStyleScheme.rubik,
    text_align: MyTextAlign.Center,
    vertical_align: MyTextVerticalAlign.Center,
    outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
    outline_color: ColorScheme.white,
    fit_font_size: true,
    min_font_size: LayoutScaler.textSizeFromDesign( 26 ),
    line_height: 0.95,
    auto_wrap: true,
    shadow_offset_x: 0,
    shadow_offset_y: 0
  } );
  substrate.addChild( text, true );

  const tail_x = Math.max( 0, Math.min( anchor_center_x - x - tail_w / 2, w - tail_w ) );
  const tail = new UIImage( ctx, tail_x, h + tail_y_offset, tail_w, tail_h, ImgID.UI_TooltipArrow );
  body.addChild( tail, true );

  root.addChild( dismiss_layer );
  root.addChild( body );
  root.invalidateSubtree();

  opened = { root, dismiss_layer, body };
}

export function hideOfflineAdTooltip(): void
{
  if ( !opened ) return;

  const { root, dismiss_layer, body } = opened;
  opened = null;

  root.removeChild( dismiss_layer );
  root.removeChild( body );
  body.cleanup();
  root.invalidateSubtree();
}
